import { useState } from "react";
import { DollarSign, Calculator, ExternalLink } from "lucide-react";
import { Card, Input, Slider, Tabs, Button } from "antd";
import "./CostEstimator.css";

const { TabPane } = Tabs;

export const CostEstimator = ({ data, onDataChange }) => {
  const parsedTuition = data.Average_Tuition_fees?.match(/\$?([\d,]+)/);
  const parsedLiving =
    data.Campus_Location?.Cost_Of_Living?.match(/\$?([\d,]+)/);

  const [amount, setAmount] = useState(
    parsedTuition ? parseInt(parsedTuition[1].replace(/,/g, ""), 10) : 0
  );
  const [cof, setCof] = useState(
    parsedLiving ? parseInt(parsedLiving[1].replace(/,/g, ""), 10) : 0
  );

  const [loanAmount, setLoanAmount] = useState(amount);
  const [loanTerm, setLoanTerm] = useState(10);
  const [interestRate, setInterestRate] = useState(10);
  const [partTimeIncome, setPartTimeIncome] = useState(
    data.partTimeIncome || 0
  );
  const [scholarship, setScholarship] = useState(data.scholarship || 0);

  // Ensure non-negative values for inputs
  const handleInputChange = (setter, key) => (e) => {
    const value = Number(e.target.value);
    if (value >= 0) {
      setter(value);
      if (onDataChange) {
        onDataChange({
          ...data,
          [key]: value,
        });
      }
    }
  };

  const totalCost = amount + cof;
  const netCost = totalCost - partTimeIncome - scholarship;

  const monthlyInterest = interestRate / 100 / 12;
  const totalPayments = loanTerm * 12;
  const monthlyPayment =
    (loanAmount *
      monthlyInterest *
      Math.pow(1 + monthlyInterest, totalPayments)) /
    (Math.pow(1 + monthlyInterest, totalPayments) - 1);

  return (
    <Card className="cost-estimator-card">
      <div className="cost-estimator-header">
        <h2 className="cost-estimator-title">Cost Estimator</h2>
      </div>
      <div className="cost-estimator-content">
        <Tabs defaultActiveKey="estimate" className="cost-estimator-tabs">
          <TabPane tab="Cost Estimate" key="estimate">
            <div className="estimator-grid">
              <div className="input-section">
                <div className="input-group">
                  <span className="inputLabel">Tuition Fee (USD/year)</span>
                  <div className="input-wrapper">
                    <Input
                      id="tuition"
                      type="number"
                      value={amount}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value >= 0) {
                          setAmount(value);
                          onDataChange?.({
                            ...data,
                            Average_Tuition_fees: `$${value}`,
                          });
                        }
                      }}
                      prefix={<DollarSign className="input-icon" />}
                      className="custom-input"
                      aria-label="Tuition Fee in USD per year"
                      min={0}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <span className="inputLabel">Cost of Living (USD/year)</span>
                  <div className="input-wrapper">
                    <Input
                      id="living"
                      type="number"
                      value={cof}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value >= 0) {
                          setCof(value);
                          onDataChange?.({
                            ...data,
                            Campus_Location: {
                              ...data.Campus_Location,
                              Cost_Of_Living: `$${value}`,
                            },
                          });
                        }
                      }}
                      prefix={<DollarSign className="input-icon" />}
                      className="custom-input"
                      aria-label="Cost of Living in USD per year"
                      min={0}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <span className="inputLabel">
                    Part-time Income (USD/year)
                  </span>
                  <div className="input-wrapper">
                    <Input
                      id="partTime"
                      type="number"
                      value={partTimeIncome}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        if (val >= 0) {
                          setPartTimeIncome(val);
                          onDataChange?.({ ...data, partTimeIncome: val });
                        }
                      }}
                      prefix={<DollarSign className="input-icon" />}
                      className="custom-input"
                      aria-label="Part-time Income in USD per year"
                      min={0}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <span className="inputLabel">Scholarships (USD/year)</span>
                  <div className="input-wrapper">
                    <Input
                      id="scholarship"
                      type="number"
                      value={scholarship}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        if (val >= 0) {
                          setScholarship(val);
                          onDataChange?.({ ...data, scholarship: val });
                        }
                      }}
                      prefix={<DollarSign className="input-icon" />}
                      className="custom-input"
                      aria-label="Scholarships in USD per year"
                      min={0}
                    />
                  </div>
                </div>
              </div>

              <div className="summary-section">
                <h3 className="summary-title">Your Cost Summary</h3>
                <div className="summary-content">
                  <div className="summary-row">
                    <span className="summary-label">Tuition Fee:</span>
                    <span className="summary-value">${amount}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Cost of Living:</span>
                    <span className="summary-value">${cof}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Total Cost:</span>
                    <span className="summary-value">${amount + cof}</span>
                  </div>
                  <div className="divider"></div>
                  <div className="summary-row">
                    <span className="summary-label">Part-time Income:</span>
                    <span className="summary-value income">
                      -${partTimeIncome}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Scholarships:</span>
                    <span className="summary-value income">
                      -${scholarship}
                    </span>
                  </div>
                  <div className="divider"></div>
                  <div className="summary-row total">
                    <span className="summary-label total">
                      Net Cost per Year:
                    </span>
                    <span className="summary-value total">${netCost}</span>
                  </div>
                  <div className="summary-row subtext">
                    <span className="summary-label subtext">
                      Total for 4-year program:
                    </span>
                    <span className="summary-value subtext">
                      ${netCost * 4}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>

          <TabPane tab="Loan Calculator" key="loan">
            <div className="estimator-grid">
              <div className="input-section">
                <div className="input-group">
                  <span className="inputLabel">Loan Amount (USD)</span>
                  <div className="input-wrapper">
                    <Input
                      id="loanAmount"
                      type="number"
                      value={loanAmount}
                      onChange={(e) =>
                        handleInputChange(setLoanAmount)(e.target.value)
                      }
                      prefix={<DollarSign className="input-icon" />}
                      className="custom-input"
                      aria-label="Loan Amount in USD"
                      min={0}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <div className="slider-label">
                    <span className="inputLabel">
                      Loan Term (years): {loanTerm}
                    </span>
                  </div>
                  <Slider
                    min={1}
                    max={25}
                    step={1}
                    value={loanTerm}
                    onChange={(value) => setLoanTerm(value)}
                    className="custom-slider"
                    aria-label={`Loan Term: ${loanTerm} years`}
                  />
                </div>

                <div className="input-group">
                  <div className="slider-label">
                    <span className="inputLabel">
                      Interest Rate (%): {interestRate}%
                    </span>
                  </div>
                  <Slider
                    min={1}
                    max={15}
                    step={0.1}
                    value={interestRate}
                    onChange={(value) => setInterestRate(value)}
                    className="custom-slider"
                    aria-label={`Interest Rate: ${interestRate}%`}
                  />
                </div>

                <Button className="loan-button">
                  Get Loan Offers
                  <ExternalLink className="button-icon" />
                </Button>
              </div>

              <div className="summary-section">
                <h3 className="summary-title">Loan Repayment Summary</h3>
                <div className="summary-content">
                  <div className="summary-row">
                    <span className="summary-label">Loan Amount:</span>
                    <span className="summary-value">
                      ${loanAmount.toLocaleString()}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Loan Term:</span>
                    <span className="summary-value">{loanTerm} years</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Interest Rate:</span>
                    <span className="summary-value">{interestRate}%</span>
                  </div>
                  <div className="divider"></div>
                  <div className="summary-row total">
                    <span className="summary-label total">
                      Monthly Payment:
                    </span>
                    <span className="summary-value total">
                      $
                      {isNaN(monthlyPayment)
                        ? "0.00"
                        : monthlyPayment.toFixed(2)}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Total Payments:</span>
                    <span className="summary-value">
                      $
                      {isNaN(monthlyPayment)
                        ? "0.00"
                        : (monthlyPayment * totalPayments).toFixed(2)}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Total Interest:</span>
                    <span className="summary-value">
                      $
                      {isNaN(monthlyPayment)
                        ? "0.00"
                        : (monthlyPayment * totalPayments - loanAmount).toFixed(
                            2
                          )}
                    </span>
                  </div>
                  <div className="summary-footer">
                    <Calculator className="footer-icon" />
                    <span className="footerText">
                      Calculations are estimates only
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>
        </Tabs>
      </div>
    </Card>
  );
};

CostEstimator.defaultProps = {
  data: {
    tuition: 56169,
    living: 25000,
    partTimeIncome: 10000,
    scholarship: 15000,
  },
  onDataChange: () => {},
};
